import React, { useEffect, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { FiCheckCircle, FiX, FiPhoneCall, FiPrinter } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { BUSINESS_WHATSAPP_CHAT_URL, BUSINESS_PHONE_MAIN } from '../../config/businessInfo';
import OrderReceiptModal from './OrderReceiptModal';
import './SuccessModal.css';

const LOTTIE_SUCCESS_URL = 'https://res.cloudinary.com/fl3tts9v/raw/upload/v1791147615/Success_irjtyx.lottie';

const SuccessModal = ({
    isOpen,
    onClose,
    title = 'Inquiry Submitted Successfully!',
    message = 'Thank you for reaching out to Rahber Corporation. Our showroom specialists will review your details and contact you shortly via WhatsApp or phone.',
    referenceId,
    phone,
    productName,
    orderTotal,
    isOrder = false,
    order = null,
}) => {
    const [isReceiptOpen, setIsReceiptOpen] = useState(false);
    const [receiptPrintKey, setReceiptPrintKey] = useState(0);

    useEffect(() => {
        if (!isOpen) {
            setIsReceiptOpen(false);
            setReceiptPrintKey(0);
            return undefined;
        }
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !isReceiptOpen) onClose();
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, isReceiptOpen]);

    if (!isOpen) return null;

    const handlePrintReceipt = () => {
        setIsReceiptOpen(true);
        setReceiptPrintKey((k) => k + 1);
    };

    const whatsappText = isOrder
        ? `Hello Rahber Corporation, I just placed an order on your website for ${productName || 'appliances'} (Order Ref: #${referenceId || 'NEW'}${orderTotal ? `, Total: ${orderTotal}` : ''}). Please confirm my order and delivery schedule.`
        : `Hello Rahber Corporation, I just submitted an inquiry on your website${productName ? ` for ${productName}` : ''}${referenceId ? ` (Ref: #${referenceId})` : ''}. Please guide me on next steps.`;

    return (
        <div className="success-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
            <div className="success-modal-card" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="success-modal-close"
                    onClick={onClose}
                    aria-label="Close dialog"
                >
                    <FiX />
                </button>

                {/* Animated Lottie Success Animation */}
                <div className="success-lottie-container">
                    <DotLottieReact
                        src={LOTTIE_SUCCESS_URL}
                        autoplay
                        loop={false}
                        className="success-dotlottie-player"
                    />
                </div>

                <div className="success-modal-content">
                    <span className="success-badge">
                        <FiCheckCircle /> {isOrder ? 'Order Confirmed & Logged' : 'Verified Submission'}
                    </span>
                    <h2 className="success-modal-title">{title}</h2>
                    <p className="success-modal-desc">{message}</p>

                    {/* Reassurance Comfort Banner */}
                    <div className="success-reassurance-box">
                        <strong>Aap Bilkul Befikar Rahain!</strong>
                        <span>
                            Hamari verified showroom team aap se foran rabta karegi. Agar aap ko foran delivery time confirm karna hai to direct WhatsApp par message karein:
                        </span>
                    </div>

                    {(productName || phone || referenceId || orderTotal) && (
                        <div className="success-modal-meta">
                            {referenceId && (
                                <div className="success-meta-row">
                                    <span className="meta-label">Order / Ref ID:</span>
                                    <strong className="meta-val meta-val--mono">#{referenceId}</strong>
                                </div>
                            )}
                            {productName && (
                                <div className="success-meta-row">
                                    <span className="meta-label">Selected Item:</span>
                                    <strong className="meta-val">{productName}</strong>
                                </div>
                            )}
                            {orderTotal && (
                                <div className="success-meta-row">
                                    <span className="meta-label">Total Payable:</span>
                                    <strong className="meta-val" style={{ color: '#EA580C' }}>{orderTotal}</strong>
                                </div>
                            )}
                            {phone && (
                                <div className="success-meta-row">
                                    <span className="meta-label">Contact Number:</span>
                                    <strong className="meta-val">{phone}</strong>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="success-modal-actions">
                        <a
                            href={`${BUSINESS_WHATSAPP_CHAT_URL}?text=${encodeURIComponent(whatsappText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-success-whatsapp"
                        >
                            <FaWhatsapp /> <span>Connect with Showroom on WhatsApp</span>
                        </a>

                        {isOrder && order && (
                            <button
                                type="button"
                                className="btn-success-print"
                                onClick={handlePrintReceipt}
                            >
                                <FiPrinter /> <span>Print Receipt</span>
                            </button>
                        )}

                        <button
                            type="button"
                            className="btn-success-done"
                            onClick={onClose}
                        >
                            Continue Shopping
                        </button>
                    </div>

                    <div className="success-modal-footer">
                        <span>Need instant helpline assistance? Call us directly: </span>
                        <a href={`tel:${Array.isArray(BUSINESS_PHONE_MAIN) ? BUSINESS_PHONE_MAIN[0].tel : BUSINESS_PHONE_MAIN}`}>
                            <FiPhoneCall /> {Array.isArray(BUSINESS_PHONE_MAIN) ? BUSINESS_PHONE_MAIN[0].display : BUSINESS_PHONE_MAIN}
                        </a>
                    </div>
                </div>
            </div>

            {isOrder && order && (
                <OrderReceiptModal
                    isOpen={isReceiptOpen}
                    onClose={() => setIsReceiptOpen(false)}
                    order={order}
                    autoPrintKey={receiptPrintKey}
                />
            )}
        </div>
    );
};

export default SuccessModal;
